import styled from '@emotion/styled';
import { Button } from '@modernhealth/vital-components';
import { Input } from 'antd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { color, font } from 'src/shared/styles/variables';

const HeaderText = styled.h4(
    {
        color: color.ebonyClay,
        marginTop: 15,
        marginBottom: 22,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

const TextArea = styled(Input.TextArea)({
    '&.ant-input': {
        minHeight: 350,
        maxHeight: 350,
        borderRadius: 4,
        padding: '10px 12px',
        border: `solid 2px ${color.linkWaterLight}`,
        color: color.steelGray,
        ...font.medium(font.size.basePlus),
    },
    '&::placeholder': {
        lineHeight: '28px',
        ...font.medium(font.size.basePlus),
    },
});

interface Props {
    onContinueClick: () => void;
    onTextChange: (text: string) => void;
    onSkipClick?: () => void;
    textValue: string;
    loading: boolean;
    isPamm?: boolean;
}

export const RateSessionTextFeedbackStep: React.FC<Props> = ({
    onContinueClick,
    onTextChange,
    onSkipClick,
    textValue,
    loading,
    isPamm,
}) => {
    const { t } = useTranslation();
    const flags = useFlags();

    const title =
        flags?.showAlternateFeedbackCopy || isPamm
            ? 'rate_session.text_feedback.feedback'
            : 'rate_session.text_feedback.private_feedback';
    const placeholder =
        flags?.showAlternateFeedbackCopy || isPamm
            ? 'rate_session.text_feedback.feedback.placeholder'
            : 'rate_session.text_feedback.private_feedback.placeholder';

    return (
        <React.Fragment>
            <HeaderText>{t(`videoSessions:${title}`)}</HeaderText>

            <TextArea
                placeholder={t(`videoSessions:${placeholder}`)}
                value={textValue}
                onChange={e => onTextChange(e.target.value)}
            />

            {isPamm && (
                <FirstButtonWrapper>
                    <Button
                        onClick={onSkipClick || onContinueClick}
                        fluid
                        size="large"
                        variant="flat"
                    >
                        {t('common.skip')}
                    </Button>
                </FirstButtonWrapper>
            )}
            <SecondButtonWrapper>
                <Button
                    onClick={onContinueClick}
                    fluid
                    disabled={isPamm && textValue.length === 0}
                    busy={loading}
                    size="large"
                >
                    {t('common.continue')}
                </Button>
            </SecondButtonWrapper>
        </React.Fragment>
    );
};

const FirstButtonWrapper = styled.div({
    marginTop: 35,
    width: '100%',
});

const SecondButtonWrapper = styled.div({
    marginTop: 10,
    width: '100%',
});
